import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ChatMessage } from '../IdeationService';
import { compareVersions } from '../../../common/utils';
import { setVersionedReports } from '../../NewIdeationXPLR/state/ideationFormSlice';

export const useVersionSync = (chatId: string, messages: ChatMessage[]) => {
  // Tracks which version the user has manually selected, keyed by chatId
  // so it resets automatically when the chat changes.
  const [manualSelection, setManualSelection] = useState<Record<string, string>>({});
  const [fileSummary, setFileSummary] = useState<string>('');
  const dispatch = useDispatch();

  // Fully derived from messages — recomputes whenever messages change,
  // and naturally returns {} for a new chatId before its messages arrive.
  const versionData = useMemo<Record<string, any>>(() => {
    const result: Record<string, any> = {};
    messages?.forEach((message: ChatMessage) => {
      if (
        message?.discoveryContent &&
        Object.keys(message.discoveryContent).length > 0 &&
        message?.version &&
        message.discoveryContent?.report?.length > 0
      ) {
        result[message.version] = {
          summary: message.discoveryContent.report || '',
          impact: message.impact || '',
        };
      }
    });
    return result;
  }, [messages]);

  // Derived: the latest version key from versionData.
  const latestVersion = useMemo(() => {
    const keys = Object.keys(versionData);
    if (!keys.length) return '';
    return keys.sort(compareVersions).at(-1) ?? keys[keys.length - 1];
  }, [versionData]);

  // Derived: use the manual selection for this chatId if set,
  // otherwise fall back to the latest version.
  // When chatId changes, manualSelection[chatId] is undefined → falls back to latestVersion.
  const currentVersion = manualSelection[chatId] || latestVersion;

  const handleVersionChange = (version: string, versionFileSummary: string = '') => {
    setManualSelection((prev) => ({ ...prev, [chatId]: version }));
    if (versionFileSummary) setFileSummary(versionFileSummary);
  };

  // Legitimate side-effect: syncing derived state to an external system (Redux).
  useEffect(() => {
    if (Object.keys(versionData).length > 0 && chatId) {
      dispatch(
        setVersionedReports({
          usecaseId: chatId,
          versionedReports: versionData,
        })
      );
    }
  }, [versionData, chatId, dispatch]);

  return {
    currentVersion,
    setCurrentVersion: (version: string) =>
      setManualSelection((prev) => ({ ...prev, [chatId]: version })),
    versionData,
    setVersionData: () => {},
    fileSummary,
    setFileSummary,
    handleVersionChange,
    createVersionData: () => {},
  };
};
